import cookies from 'js-cookie';
import { FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clearTimeout, setTimeout } from 'worker-timers';

import { AnalyticsDimensions, logAnalyticsEvent } from '@vestico/utils';

import { defaultCookieAttributes } from '@vestico/model';

import { AuthAPIContext, WidgetAuthContext } from './MainContext';
import { ConfigContext } from './WidgetEventsManager';
import { AuthInfo, authInfoIsValid } from './model/api';
import { AuthApiClient } from './services/apiClientAuth';
import { vesticoAuthCookieKey } from './services/helpers';

const getAuthInfoFromCookies = (clientId: string | undefined) => {
  const existingAuthInfoString = cookies.get(vesticoAuthCookieKey);
  if (clientId && existingAuthInfoString && existingAuthInfoString !== undefined) {
    const existingAuthInfo: AuthInfo = JSON.parse(existingAuthInfoString);
    const existingAuthInfoIsValid = authInfoIsValid(existingAuthInfo, clientId);
    if (existingAuthInfoIsValid) {
      console.debug(`Using auth info from cookies. Expires: ${existingAuthInfo.expiryDate}`);
      return existingAuthInfo;
    }
  }
  return null;
};

export const AuthContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { isPreview, clientId, iOSBundleIdentifier, sdkAPIKey, experiments } = useContext(ConfigContext);
  const authAPI = useRef(new AuthApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments));

  const [refetchAuthInfo, setRefetchAuthInfo] = useState(false); // set to true if product fetch fails due to expired token. Don't set back to false
  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(getAuthInfoFromCookies(clientId));
  const refetchAuthInfoTimerIDsRef = useRef<Set<number>>(new Set());

  useLayoutEffect(() => {
    authAPI.current = new AuthApiClient(clientId, isPreview, sdkAPIKey, iOSBundleIdentifier, experiments);
  }, [clientId, experiments, iOSBundleIdentifier, isPreview, sdkAPIKey]);

  const forceReloadAuthInfo = useCallback(() => {
    console.debug('removing auth info');
    cookies.remove(vesticoAuthCookieKey);
  }, []);

  const updateTimer = useCallback(
    (timerAuthInfo: AuthInfo) => {
      const { expiryDate } = timerAuthInfo;

      const { current: refetchAuthInfoTimerIDs } = refetchAuthInfoTimerIDsRef;
      if (refetchAuthInfoTimerIDs) {
        for (const refetchAuthInfoTimerID of refetchAuthInfoTimerIDs) {
          clearTimeout(refetchAuthInfoTimerID);
          refetchAuthInfoTimerIDs.delete(refetchAuthInfoTimerID);
        }
      }

      // force to refetch auth info once it expires
      const delay = expiryDate - Date.now() - 60 * 1000;
      const newId = setTimeout(
        () => {
          setAuthInfo(null);
          forceReloadAuthInfo();
          refetchAuthInfoTimerIDs.delete(newId);
        },
        delay > 0 ? delay : 1000
      );
      refetchAuthInfoTimerIDs.add(newId);
    },
    [forceReloadAuthInfo]
  );

  const fetchAuthInfoIfNeeded = useCallback(
    async (force = false, silentReplace = false) => {
      // If auth info is still valid, don't do anything
      if (!force && authInfoIsValid(authInfo, clientId)) {
        // eslint-disable-next-line camelcase
        console.info(`got auth info ${authInfo?.expiryDate}`);
        return authInfo;
      }

      // Check cookies if we don't force refresh
      if (!force) {
        const existingAuthInfo = getAuthInfoFromCookies(clientId);
        if (existingAuthInfo) {
          setAuthInfo(existingAuthInfo);
          return existingAuthInfo;
        }
      }

      // Fetch new auth info
      console.debug('getting new auth info');
      const newAuthInfo = await authAPI.current.fetchAuthInfo(refetchAuthInfo);

      // reset refetch auth info
      setRefetchAuthInfo(false);

      // If the fetch failed, let's set everything to null
      if (!newAuthInfo) {
        console.error('Could not fetch auth info');
        logAnalyticsEvent({ category: 'exception', event: 'fetch_auth_failed', dimensions: { provider: clientId } });
        setAuthInfo(null);
        return null;
      }

      console.debug(`got new auth info: ${newAuthInfo.expiryDate}`);
      const { expiryDate } = newAuthInfo;

      // check if the freshly fetched auth info is not valid
      if (!authInfoIsValid(newAuthInfo, clientId)) {
        const dimensions: AnalyticsDimensions = {
          provider: clientId,
        };
        logAnalyticsEvent({ category: 'exception', event: 'new_token_invalid', dimensions: dimensions });

        // If we're not already refetching auth info, try one more time
        if (!refetchAuthInfo) {
          setRefetchAuthInfo(true);
          return null;
        }

        // Otherwise just set auth info to null and wait until it's requested again
        setAuthInfo(null);
        return null;
      }

      if (expiryDate) {
        const attributes = defaultCookieAttributes();
        attributes.expires = new Date(expiryDate);
        cookies.set(vesticoAuthCookieKey, JSON.stringify(newAuthInfo), attributes);

        updateTimer(newAuthInfo);
      }

      if (silentReplace && authInfo) {
        authInfo.apiKey = newAuthInfo.apiKey;
        authInfo.expiryDate = newAuthInfo.expiryDate;
        authInfo.token = newAuthInfo.token;
      } else {
        setAuthInfo(newAuthInfo);
      }
      return newAuthInfo;
    },
    [authInfo, clientId, refetchAuthInfo, updateTimer]
  );

  const resetAuth = useCallback(() => {
    return fetchAuthInfoIfNeeded(true, true);
  }, [fetchAuthInfoIfNeeded]);

  useEffect(() => {
    fetchAuthInfoIfNeeded();
  }, [fetchAuthInfoIfNeeded]);

  useEffect(() => {
    if (!refetchAuthInfo) {
      return;
    }
    forceReloadAuthInfo();
    fetchAuthInfoIfNeeded(true);
  }, [refetchAuthInfo]);

  const widgetAuthContextValue = useMemo(() => {
    return { authInfo, resetAuth };
  }, [authInfo, resetAuth]);

  return (
    <AuthAPIContext.Provider value={authAPI.current}>
      <WidgetAuthContext.Provider value={widgetAuthContextValue}>{children}</WidgetAuthContext.Provider>
    </AuthAPIContext.Provider>
  );
};
